import { RigModel, band, hsl, idHash, mix, scale, WHITE, type Animator, type Gait, type Palette, type Part, type Pose, type Rig, type Shape } from "./Rig";
import { pixelModel } from "./pixelArt";

/**
 * The grazer: a longneck, llama-ish. A lean body high on knobbly legs, a
 * shaggy neck that swings down to the grass or stretches up into the low
 * branches, a long face with horns and an ear that flicks. Feet at the
 * origin, up is +y, it faces +x.
 *
 * Every joint is a disc or a capsule's round end hinged from inside the
 * part it hangs on: the hips sit inside the body, the head's disc is the
 * top of the neck. The neck itself bends (see Bend) rather than swinging
 * at the shoulder, so it stays all one with the body as it reaches down.
 */

const HIP = 1.55;

/** Shaggy tufts up the back of the neck. */
function tufts(n: number): Shape[] {
  return Array.from({ length: n }, (_, i) => ({ color: "wool", at: [-0.32 + i * 0.16, -0.05 + i * 0.36], form: { kind: "disc", r: 0.2, ry: 0.13, rot: 0.9 } }));
}

/** A leg hung from the hip at `x`: thigh, knee, shin and hoof in `skin` and `hoof`. */
function leg(name: string, x: number, z: number, skin: string, hoof: string): Part {
  return {
    name,
    parent: "root",
    at: [x, HIP],
    z,
    moves: true,
    shapes: [
      { color: skin, form: { kind: "capsule", to: [0, -0.78], width: 0.3 } },
      { color: skin, at: [0, -0.78], form: { kind: "capsule", to: [0, -0.67], width: 0.18 } },
      { color: hoof, at: [0, -1.45], form: { kind: "path", ops: [["M", -0.14, 0.03], ["L", 0.14, 0.03], ["L", 0.16, -0.13], ["L", -0.16, -0.13], ["Z"]] } },
    ],
  };
}

export const LONGNECK: Rig = {
  parts: [
    // Everything hangs from here, so the whole beast can bounce as it walks.
    { name: "root", at: [0, 0], z: 0, moves: true, shapes: [] },
    leg("farHind", -0.65, 0, "far", "hoofFar"),
    leg("farFore", 0.5, 1, "far", "hoofFar"),
    {
      name: "tail",
      parent: "root",
      at: [-1.05, 1.95],
      z: 2,
      moves: true,
      shapes: [
        { color: "skin", form: { kind: "stroke", width: 0.08, ops: [["M", 0, 0], ["Q", -0.3, -0.2, -0.25, -0.6]] } },
        { color: "hoof", at: [-0.25, -0.65], form: { kind: "disc", r: 0.12, ry: 0.16 } },
      ],
    },
    {
      name: "body",
      parent: "root",
      at: [0, 1.75],
      z: 3,
      shapes: [
        { color: "wool", form: { kind: "disc", r: 1.05, ry: 0.5 } },
        { color: "saddle", form: band(1.0, 0.5, 0.45, true) },
        { color: "pale", at: [0, -0.05], form: band(0.85, 0.45, 0.55, false) },
        { color: "wool", at: [-0.7, 0.15], form: { kind: "disc", r: 0.42, ry: 0.38 } },
      ],
    },
    leg("nearHind", -0.5, 4, "skin", "hoof"),
    leg("nearFore", 0.65, 5, "skin", "hoof"),
    {
      name: "neck",
      parent: "root",
      at: [0.75, 2.05],
      z: 6,
      // Up the middle of the neck to under the head: it curls along this.
      spine: [0.65, 1.55],
      shapes: [
        // The shoulder, filling the crook where the neck meets the body.
        { color: "wool", form: { kind: "disc", r: 0.38 } },
        { color: "wool", form: { kind: "path", ops: [["M", -0.4, -0.25], ["C", -0.2, 0.5, 0, 1, 0.35, 1.5], ["Q", 0.6, 1.75, 0.95, 1.6], ["C", 0.7, 0.9, 0.5, 0.3, 0.3, -0.35], ["Z"]] } },
        { color: "saddle", form: { kind: "path", ops: [["M", -0.4, -0.25], ["C", -0.2, 0.5, 0, 1, 0.35, 1.5], ["Q", 0.45, 1.5, 0.5, 1.35], ["C", 0.2, 0.9, 0, 0.4, -0.1, -0.3], ["Z"]] } },
        // Shaggy along the back of the neck.
        ...tufts(5),
        // The top of the neck, round under the head.
        { color: "wool", at: [0.65, 1.55], form: { kind: "disc", r: 0.28 } },
      ],
    },
    {
      name: "head",
      parent: "neck",
      at: [0.7, 1.68],
      z: 8,
      moves: true,
      shapes: [
        { color: "skin", form: { kind: "disc", r: 0.3 } },
        { color: "skin", form: { kind: "path", ops: [["M", -0.35, 0.28], ["C", 0.1, 0.42, 0.55, 0.3, 0.72, 0.06], ["Q", 0.7, -0.12, 0.55, -0.16], ["C", 0.2, -0.22, -0.2, -0.22, -0.4, -0.05], ["Z"]] } },
        { color: "hoof", at: [0.63, 0], form: { kind: "disc", r: 0.08, ry: 0.06 } },
        { color: "hoof", form: { kind: "stroke", width: 0.04, ops: [["M", 0.62, -0.1], ["Q", 0.45, -0.16, 0.3, -0.12]] } },
        { color: "pale", at: [0.12, 0.14], form: { kind: "disc", r: 0.12 } },
        { color: "eye", at: [0.14, 0.14], form: { kind: "disc", r: 0.075 } },
        { color: "hoof", form: { kind: "stroke", width: 0.07, ops: [["M", 0.05, 0.3], ["Q", 0.2, 0.62, 0.42, 0.7], ["M", -0.05, 0.3], ["Q", 0.02, 0.6, 0.25, 0.75]] } },
        { color: "wool", at: [-0.1, 0.3], form: { kind: "disc", r: 0.25, ry: 0.17 } },
      ],
    },
    {
      name: "ear",
      parent: "head",
      at: [-0.15, 0.3],
      z: 7,
      moves: true,
      shapes: [{ color: "skin", form: { kind: "path", ops: [["M", 0, 0], ["L", -0.32, 0.5], ["L", 0.12, 0.12], ["Z"]] } }],
    },
  ],
};

export const LONGNECK_MODEL = pixelModel(LONGNECK);

/** Each one's coat: warm wool from cream to tan, darker skin. */
export function longneckColors(id: string): Palette {
  const wool = hsl(0.08 + idHash(id, 1) * 0.06, 0.25 + idHash(id, 2) * 0.2, 0.62 + idHash(id, 3) * 0.2);
  const skin = hsl(0.06, 0.3, 0.22 + idHash(id, 4) * 0.12);
  const hoof = scale(skin, 0.55);
  return { wool, saddle: scale(wool, 0.82), pale: mix(wool, WHITE, 0.3), skin, far: scale(skin, 0.7), hoof, hoofFar: scale(hoof, 0.7), eye: [0.07, 0.05, 0.03] };
}

/**
 * How a longneck moves: legs stepping in pairs and the body bouncing as it
 * walks; eating, the neck swings down to graze or (now and then) up to
 * browse, chewing at either end; its tail swings and an ear flicks.
 */
export class LongneckAnimator implements Animator<Gait> {
  private t: number;
  private stride = 0;
  private reach = 0;
  private browsing = false;
  private untilTurn = 0;
  private readonly phase: number;

  constructor(seed = Math.random()) {
    this.t = seed * 10;
    this.phase = seed * 6;
  }

  step(dt: number, g: Gait): Pose {
    this.t += dt;
    const walking = g.speed > 0.4;
    if (walking) this.stride += dt * g.speed * 3.2;
    const swing = walking ? Math.min(1, g.speed / 2) * 0.44 : 0;
    const bounce = walking ? Math.abs(Math.sin(this.stride)) * 0.08 : 0;
    // Eating: mostly the grass, sometimes the leaves, a few seconds at each.
    this.untilTurn -= dt;
    if (this.untilTurn <= 0) {
      this.untilTurn = 3 + Math.random() * 4;
      this.browsing = Math.random() < 0.4;
    }
    const want = g.eating ? (this.browsing ? -1 : 1) : 0;
    this.reach += (want - this.reach) * (1 - Math.exp(-dt * 3));
    const r = this.reach;
    const chew = g.eating && Math.abs(r) > 0.8 ? Math.sin(this.t * 7) * 0.05 : Math.sin(this.t * 0.6) * 0.04;
    const step = (phase: number) => Math.sin(this.stride + phase) * swing;
    return {
      root: { y: bounce },
      farFore: { rot: step(0.6) },
      farHind: { rot: step(Math.PI + 0.6) },
      nearFore: { rot: step(0) },
      nearHind: { rot: step(Math.PI) },
      tail: { rot: Math.sin(this.t * 2.3) * 0.12 },
      // At rest the neck leans forward; grazing it bends down to the grass, browsing it arches back past upright.
      neck: { bend: -0.35 + (r > 0 ? -r * 2.45 : -r * 0.55) + chew },
      head: { rot: r > 0 ? r * 0.9 : 0 },
      ear: { rot: Math.pow(Math.max(0, Math.sin(this.t * 0.9 + this.phase)), 10) * 0.6 },
    };
  }
}
